import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Calendar, Search, Edit, Trash2, Eye, FileText, X } from 'lucide-react';
import { ModuleInfo } from './ui/module-info';
import { usePersistedState } from '../hooks/usePersistedState';
import api from '../services/api';
import axios from 'axios';
import { toast } from 'sonner';
import { FechoTurnoViewModal, calcularDiferencaFecho, VALORES_RASPADINHAS } from './fecho-turno';
import { calcularFechoTotais } from '../utils/fechoTurnoCalc';
import { handleNumericKeyDown, processNumericInput } from '../utils/inputUtils';
import { Pagination } from './ui/pagination';
import { getBackendUrl } from '../utils/config';

const API_URL = getBackendUrl();

const FechosTurnosTab = ({ autoOpenModal = false, onModalClose = () => {} }) => {
  const [fechos, setFechos] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Paginação
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1, limit: 50 });
  
  const [filters, setFilters] = usePersistedState('fechos_filtros', {
    dataInicio: '',
    dataFim: '',
    colaboradorId: '',
    turno: ''
  });
  const [selectedFecho, setSelectedFecho] = useState(null);
  const [fechoManha, setFechoManha] = useState(null); // Fecho da manhã para cálculos da tarde
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [jogosData, setJogosData] = useState(null);

  // Helper para atualizar campos numéricos do editData - aceita vírgulas
  const updateEditDataNumeric = (campo, valor) => {
    const valorProcessado = processNumericInput(valor);
    setEditData({...editData, [campo]: valorProcessado});
  };

  // Função para buscar fecho da manhã quando seleccionar um fecho da tarde
  const buscarFechoManha = async (fecho) => {
    if (!fecho || !fecho.turno?.toLowerCase().includes('tarde')) {
      setFechoManha(null);
      return null;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Buscar TODOS os fechos do mesmo dia
      const response = await axios.get(`${API_URL}/api/fechos-turno`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { 
          mes: new Date(fecho.data).getMonth() + 1,
          ano: new Date(fecho.data).getFullYear()
        }
      });
      
      const todosFechos = response.data || [];
      // Filtrar pelo mesmo dia e turno manhã
      const manha = todosFechos.find(f => 
        f.data === fecho.data && 
        f.turno?.toLowerCase().includes('manh')
      );
      
      setFechoManha(manha || null);
      return manha;
    } catch (error) {
      console.error('Erro ao buscar fecho da manhã:', error);
      setFechoManha(null);
      return null;
    }
  };

  // Função auxiliar para calcular valores ajustados (subtraindo manhã se for tarde)
  const getValoresAjustados = (fecho, fechoManhaParam = null) => {
    const manhaData = fechoManhaParam || fechoManha;
    let vendas = fecho?.premios_master_1_15 || 0;
    let premios = fecho?.premios_total || 0;
    
    // Se for tarde e tiver fecho da manhã, subtrair
    if (fecho?.turno?.toLowerCase().includes('tarde') && manhaData) {
      const vendasManha = manhaData.premios_master_1_15 || 0;
      const premiosManha = manhaData.premios_total || 0;
      vendas = vendas - vendasManha;
      premios = premios - premiosManha;
    }
    
    return { vendas, premios };
  };

  // Recalcular totais automaticamente quando editData muda
  // 🐛 FIX 21-06-2026: delegar no util `calcularFechoTotais` (com parseFloat
  // em todos os campos). Antes, a fórmula estava duplicada in-line sem
  // parseFloat — quando os valores vinham do Mongo como string (e.g.
  // "86.50"), o `+` do JS fazia concatenação e o Total Expectável aparecia
  // com valores absurdos (ex: 52486.50 para vendas reais de 86.50).
  useEffect(() => {
    if (!editData) return;
    const t = calcularFechoTotais(editData, editData._dadosManha || null);
    if (
      editData.valor_venda_raspadinhas !== t.valorVendaRaspadinhas ||
      editData.total_expectavel !== t.totalExpectavel ||
      editData.diferenca !== t.diferenca
    ) {
      setEditData(prev => ({
        ...prev,
        valor_venda_raspadinhas: t.valorVendaRaspadinhas,
        total_expectavel: t.totalExpectavel,
        diferenca: t.diferenca,
      }));
    }
  }, [
    editData?.caixa_inicial_1, editData?.caixa_inicial_2, editData?.caixa_final,
    editData?.comissao_revendas,
    editData?.stock_1, editData?.stock_2, editData?.stock_3, 
    editData?.stock_5, editData?.stock_10, editData?.stock_15,
    editData?.recepcao_1, editData?.recepcao_2, editData?.recepcao_3,
    editData?.recepcao_5, editData?.recepcao_10, editData?.recepcao_15,
    editData?.s_final_1, editData?.s_final_2, editData?.s_final_3,
    editData?.s_final_5, editData?.s_final_10, editData?.s_final_15,
    editData?.premios_master_1_15, editData?.premios_total,
    editData?.turno, editData?._dadosManha
  ]);

  useEffect(() => {
    fetchColaboradores();
  }, []);

  useEffect(() => {
    fetchFechos(page);
  }, [page]);

  // Auto-open modal quando prop é true
  useEffect(() => {
    if (autoOpenModal && !showCreateModal) {
      setShowCreateModal(true);
    }
  }, [autoOpenModal]);

  const fetchColaboradores = async () => {
    try {
      const response = await api.get('/users');
      // 🐛 FIX 27-06-2026: incluir TODOS os perfis activos no select de
      // colaboradores do Fecho de Turno (não só `colaborador`/`responsavel`).
      // Em produção alguns users válidos têm role diferente (admin, gerente,
      // sem role definido), e o filtro restritivo deixava o select vazio.
      // Para o ranking/operação de fechos, qualquer user activo deve poder
      // ser atribuído.
      setColaboradores(
        response.data
          .filter((u) => u.ativo !== false)
          .sort((a, b) => (a.nome || a.username || '').localeCompare(b.nome || b.username || ''))
      );
    } catch (error) {
      console.error('Erro ao buscar colaboradores:', error);
    }
  };

  const fetchFechos = useCallback(async (currentPage = 1) => {
    setLoading(true);
    try {
      const response = await api.get('/fechos-turno', {
        params: { page: currentPage, limit: 50 }
      });
      
      // Verificar se é resposta paginada
      if (response.data.items) {
        // Ordenar items
        const fechosOrdenados = (response.data.items || []).sort((a, b) => {
          const dataA = new Date(a.data);
          const dataB = new Date(b.data);
          if (dataB - dataA !== 0) return dataB - dataA;
          const turnoOrdem = { 'Tarde': 0, 'Manhã': 1 };
          return (turnoOrdem[a.turno] || 2) - (turnoOrdem[b.turno] || 2);
        });
        setFechos(fechosOrdenados);
        setPagination({
          total: response.data.total,
          pages: response.data.pages,
          limit: response.data.limit
        });
      } else {
        // Retrocompatibilidade
        const fechosOrdenados = (response.data || []).sort((a, b) => {
          const dataA = new Date(a.data);
          const dataB = new Date(b.data);
          if (dataB - dataA !== 0) return dataB - dataA;
          const turnoOrdem = { 'Tarde': 0, 'Manhã': 1 };
          return (turnoOrdem[a.turno] || 2) - (turnoOrdem[b.turno] || 2);
        });
        setFechos(fechosOrdenados);
        setPagination({ total: fechosOrdenados.length, pages: 1, limit: fechosOrdenados.length });
      }
    } catch (error) {
      toast.error('Erro ao carregar fechos de turno');
      console.error('Erro ao buscar fechos:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleFilter = () => {
    let filtered = [...fechos];

    if (filters.dataInicio) {
      filtered = filtered.filter(f => f.data >= filters.dataInicio);
    }
    if (filters.dataFim) {
      filtered = filtered.filter(f => f.data <= filters.dataFim);
    }
    if (filters.colaboradorId) {
      filtered = filtered.filter(f => f.colaborador_id === filters.colaboradorId);
    }
    if (filters.turno) {
      filtered = filtered.filter(f => f.turno === filters.turno);
    }

    return filtered;
  };

  const handleDelete = async (fechoId) => {
    if (!window.confirm('Tem certeza que deseja eliminar este fecho de turno?')) {
      return;
    }

    try {
      await api.delete(`/fechos-turno/${fechoId}`);
      toast.success('Fecho de turno eliminado com sucesso');
      fetchFechos();
    } catch (error) {
      toast.error('Erro ao eliminar fecho de turno');
      console.error('Erro ao eliminar:', error);
    }
  };

  // Função para corrigir cálculos de todos os fechos (apenas admin)
  const handleCorrigirCalculos = async () => {
    if (!window.confirm('Esta ação vai recalcular o Total Expectável e Diferença para TODOS os fechos de turno usando a fórmula correta.\n\nOs dados originais (stocks, vendas, prémios, caixa) NÃO serão alterados.\n\nDeseja continuar?')) {
      return;
    }

    try {
      toast.info('A corrigir cálculos... Por favor aguarde.');
      const response = await api.post('/fechos-turno/corrigir-calculos');
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.corrigidos} fechos corrigidos com sucesso!`);
        fetchFechos(); // Recarregar lista
      } else {
        toast.error('Erro ao corrigir cálculos');
      }
      
      if (response.data.erros && response.data.erros.length > 0) {
        console.error('Erros na correção:', response.data.erros);
        toast.warning(`${response.data.erros.length} fechos com erros`);
      }
    } catch (error) {
      toast.error('Erro ao corrigir cálculos');
      console.error('Erro:', error);
    }
  };

  const handleView = async (fecho) => {
    // Primeiro, buscar fecho da manhã se for tarde
    let manhaData = null;
    if (fecho?.turno?.toLowerCase().includes('tarde')) {
      manhaData = await buscarFechoManha(fecho);
    } else {
      setFechoManha(null);
    }
    // SÓ DEPOIS de ter os dados da manhã, mostrar o modal
    setSelectedFecho(fecho);
    setShowViewModal(true);
  };

  const handleEdit = async (fecho) => {
    setSelectedFecho(fecho);
    setShowViewModal(false);
    
    // CRÍTICO: Buscar fecho da manhã PRIMEIRO se for tarde
    let dadosManha = null;
    if (fecho?.turno?.toLowerCase().includes('tarde')) {
      dadosManha = await buscarFechoManha(fecho);
    } else {
      setFechoManha(null);
    }
    
    // SÓ DEPOIS de carregar a manhã, definir editData
    // GUARDAR dados da manhã DENTRO do editData para garantir que são usados nos cálculos
    setEditData({
      ...fecho,
      _dadosManha: dadosManha // Guardar dados da manhã para usar nos cálculos
    });
    
    // Buscar dados de jogos para o ano do fecho
    try {
      const dataFecho = new Date(fecho.data + 'T00:00:00');
      const ano = dataFecho.getFullYear();
      const response = await api.get(`/monitorizacao/jogos/${ano}`);
      setJogosData(response.data);
    } catch (error) {
      console.error('Erro ao buscar dados de jogos:', error);
      setJogosData([]);
    }
    
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      // Remover campo auxiliar _dadosManha antes de enviar
      const { _dadosManha, ...dataToSend } = editData;
      
      // Salvar fecho de turno
      await api.put(`/fechos-turno/${dataToSend.id}`, dataToSend);
      
      // Salvar dados de jogos se foram alterados
      if (jogosData && jogosData.length > 0) {
        const dataFecho = new Date(editData.data + 'T00:00:00');
        const ano = dataFecho.getFullYear();
        
        for (const jogo of jogosData) {
          try {
            // O endpoint espera: PUT /api/monitorizacao/jogos/{ano}/{jogo}
            await api.put(`/monitorizacao/jogos/${ano}/${encodeURIComponent(jogo.jogo)}`, {
              dados_semanais: jogo.dados_semanais
            });
          } catch (jogoError) {
            console.error(`❌ Erro ao atualizar jogo ${jogo.jogo}:`, jogoError);
            toast.error(`Erro ao atualizar ${jogo.jogo}`);
          }
        }
      }
      
      toast.success('Fecho de turno e jogos atualizados com sucesso');
      setShowEditModal(false);
      setJogosData(null);
      fetchFechos();
    } catch (error) {
      toast.error('Erro ao atualizar fecho de turno');
      console.error('Erro ao atualizar:', error);
    }
  };

  const filteredFechos = handleFilter();

  const formatCurrency = (value) => {
    // Evitar "-0.00" tratando valores muito próximos de zero como zero positivo
    const numValue = value || 0;
    const adjustedValue = Math.abs(numValue) < 0.01 ? 0 : numValue;
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR'
    }).format(adjustedValue);
  };

  const getColaboradorNome = (colaboradorId) => {
    const colab = colaboradores.find(c => c.id === colaboradorId);
    return colab ? colab.nome : 'N/A';
  };

  const calcularRaspVendidas = (fecho, valor) => {
    const stock = fecho[`stock_${valor}`] || 0;
    const recepcao = fecho[`recepcao_${valor}`] || 0;
    const sFinal = fecho[`s_final_${valor}`] || 0;
    return stock + recepcao - sFinal;
  };

  const calcularTotalRasp = (fecho, valor) => {
    return calcularRaspVendidas(fecho, valor) * valor;
  };

  return (
    <div className="space-y-6">
      {/* ModuleInfo - Ajuda contextual */}
      <div className="flex items-center gap-2">
        <ModuleInfo
          descricao="Historico de todos os fechos de turno realizados. Permite consultar, filtrar, editar e eliminar fechos anteriores. Inclui correcao de calculos em lote e visualizacao detalhada com separacao manha/tarde."
          dados="Fechos guardados em /api/fechos-turno com paginacao server-side. Dados de jogos via /api/monitorizacao/jogos. Colaboradores de /api/users."
          calculo="Total expectavel = caixa inicial + raspadinhas vendidas + vendas - premios + comissao revendas. Diferenca = caixa final - total expectavel. No turno da Tarde, os valores da Manha sao subtraidos automaticamente."
          legendas="Diferenca positiva (verde) = excesso de caixa. Diferenca negativa (vermelho) = falta na caixa. Botao 'Corrigir Calculos' recalcula todos os fechos no servidor."
        />
      </div>
      {/* Header com Filtros */}
      <Card className="bg-gradient-card border-primary/20">
        <CardContent className="p-4">
          <div className="flex justify-between items-start mb-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 flex-1">
              <div>
                <Label className="text-sm font-medium">Data Início</Label>
                <Input
                  type="date"
                  value={filters.dataInicio}
                  onChange={(e) => setFilters({ ...filters, dataInicio: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Data Fim</Label>
                <Input
                  type="date"
                  value={filters.dataFim}
                  onChange={(e) => setFilters({ ...filters, dataFim: e.target.value })}
                  className="mt-1"
                />
            </div>
            <div>
              <Label className="text-sm font-medium">Colaborador</Label>
              <Select
                value={filters.colaboradorId || "all"}
                onValueChange={(value) => setFilters({ ...filters, colaboradorId: value === "all" ? "" : value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {colaboradores.map((colab) => (
                    <SelectItem key={colab.id} value={colab.id}>
                      {colab.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Turno</Label>
              <Select
                value={filters.turno || "all"}
                onValueChange={(value) => setFilters({ ...filters, turno: value === "all" ? "" : value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="Manhã">Manhã</SelectItem>
                  <SelectItem value="Tarde">Tarde</SelectItem>
                </SelectContent>
              </Select>
            </div>
            </div>
            {/* Botão para corrigir cálculos */}
            <div className="ml-4 pt-5 flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleCorrigirCalculos}
                className="whitespace-nowrap text-orange-600 border-orange-300 hover:bg-orange-50"
                title="Recalcula Total Expectável e Diferença para todos os fechos"
              >
                Corrigir Cálculos
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cards de Fechos */}
      {loading ? (
        <div className="text-center py-8">Carregando...</div>
      ) : filteredFechos.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Nenhum fecho de turno encontrado</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredFechos.map((fecho) => (
            <Card key={fecho.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Turno {fecho.turno}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {new Date(fecho.data + 'T00:00:00').toLocaleDateString('pt-PT')}
                    </p>
                    <p className="text-sm font-medium text-primary mt-1">
                      👤 {fecho.colaborador_nome || getColaboradorNome(fecho.colaborador_id)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleView(fecho)}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="destructive" 
                      onClick={() => handleDelete(fecho.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Colaborador:</span>
                    <span className="font-semibold">{getColaboradorNome(fecho.colaborador_id)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Caixa Inicial:</span>
                    <span className="font-semibold">{formatCurrency(fecho.caixa_inicial_1 || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Caixa Final:</span>
                    <span className="font-semibold text-primary">{formatCurrency(fecho.caixa_final || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Diferença:</span>
                    <span className={`font-semibold ${(() => {
                      // Usar o valor de diferença guardado na BD
                      const diff = fecho.diferenca || 0;
                      // Tolerância: valores <= 0.01 são considerados zero (igual à edição)
                      if (Math.abs(diff) <= 0.01) return 'text-muted-foreground';
                      if (diff > 0) return 'text-success';
                      return 'text-destructive';
                    })()}`}>
                      {formatCurrency(Math.abs(fecho.diferenca || 0) <= 0.01 ? 0 : (fecho.diferenca || 0))}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Raspadinhas:</span>
                    <span className="font-semibold text-blue-600">
                      {(() => {
                        // Calcular total vendido: (stock_inicial + recepcao - stock_final) * valor
                        const valores = [1, 2, 3, 5, 10, 15];
                        const total = valores.reduce((sum, valor) => {
                          const stock = fecho[`stock_${valor}`] || 0;
                          const recepcao = fecho[`recepcao_${valor}`] || 0;
                          const sFinal = fecho[`s_final_${valor}`] || 0;
                          const vendidas = stock + recepcao - sFinal;
                          return sum + (vendidas * valor);
                        }, 0);
                        return formatCurrency(total);
                      })()}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Paginação */}
      {!loading && filteredFechos.length > 0 && (
        <div className="mt-4">
          <Pagination
            page={page}
            pages={pagination.pages}
            total={pagination.total}
            limit={pagination.limit}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* Modal de Visualização - Componente Extraído */}
      <FechoTurnoViewModal
        show={showViewModal}
        fecho={selectedFecho}
        onClose={() => setShowViewModal(false)}
        onEdit={handleEdit}
        calcularRaspVendidas={calcularRaspVendidas}
        calcularTotalRasp={calcularTotalRasp}
      />

      {/* Modal de Edição */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Edit className="h-5 w-5" />
                Editar Fecho de Turno
              </h2>
              <button onClick={() => setShowEditModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              {editData && (
                <div className="space-y-6">
              {/* Data, Turno e Colaborador */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Data</label>
                  <div className="px-3 py-2 border rounded-md bg-muted">
                    {new Date(editData.data + 'T00:00:00').toLocaleDateString('pt-PT')}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Turno</label>
                  <div className="px-3 py-2 border rounded-md bg-muted">
                    {editData.turno}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Colaborador</label>
                  {/* 🆕 21-06-2026: admin pode reatribuir o fecho a outro colaborador.
                      Ao guardar, o backend transfere a venda automática (e os pontos)
                      do antigo para o novo.
                      🐛 FIX 27-06-2026: mostrar TODOS os users activos no select
                      (não só os de role colaborador/responsavel), e garantir que
                      o colaborador actualmente atribuído aparece mesmo se for
                      inactivo — caso contrário o select aparecia vazio. */}
                  {(() => {
                    const opcoes = [...colaboradores];
                    const actualId = editData.colaborador_id;
                    if (actualId && !opcoes.some((c) => c.id === actualId)) {
                      opcoes.unshift({
                        id: actualId,
                        nome: editData.colaborador_nome || '(actual)',
                        username: '',
                      });
                    }
                    return (
                      <Select
                        value={editData.colaborador_id || ''}
                        onValueChange={(val) => {
                          const colab = opcoes.find((c) => c.id === val);
                          setEditData({
                            ...editData,
                            colaborador_id: val,
                            colaborador_nome:
                              colab?.nome || colab?.name || editData.colaborador_nome,
                          });
                        }}
                      >
                        <SelectTrigger data-testid="edit-fecho-colaborador-select">
                          <SelectValue placeholder="Selecionar colaborador" />
                        </SelectTrigger>
                        <SelectContent className="max-h-72 overflow-y-auto">
                          {opcoes.map((c) => (
                            <SelectItem
                              key={c.id}
                              value={c.id}
                              data-testid={`edit-fecho-colab-${c.id}`}
                            >
                              {c.nome || c.name || c.username}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    );
                  })()}
                </div>
              </div>

              {/* Caixa Inicial */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Caixa Inicial</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm mb-2 font-semibold">Caixa Inicial</label>
                      <Input
                        type="text"
                        
                        value={editData.caixa_inicial_1 || 0}
                        onChange={(e) => updateEditDataNumeric('caixa_inicial_1', e.target.value)}
                        onKeyDown={handleNumericKeyDown}
                        inputMode="decimal" pattern="[0-9]*[.,]?[0-9]*"
                        className="font-bold"
                      />
                    </div>
                    <div>
                      <label className="block text-sm mb-2 font-semibold">Caixa Atual</label>
                      <Input
                        type="text"
                        
                        value={editData.caixa_inicial_2 || 0}
                        onChange={(e) => {
                          const processed = processNumericInput(e.target.value);
                          // 🔧 iter301 — sync caixa_final = caixa_inicial_2 para que a Diferença
                          // recalcule imediatamente quando o admin altera Caixa Atual.
                          setEditData({...editData, caixa_inicial_2: processed, caixa_final: processed});
                        }}
                        onKeyDown={handleNumericKeyDown}
                        inputMode="decimal" pattern="[0-9]*[.,]?[0-9]*"
                        className="font-bold"
                      />
                    </div>
                    <div>
                      <label className="block text-sm mb-2 font-semibold">Caixa Real</label>
                      <div className="px-3 py-2 border rounded-md bg-warning/10 text-warning font-bold">
                        €{((editData.caixa_inicial_2 || 0) - (editData.caixa_inicial_1 || 0)).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Raspadinhas */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Raspadinhas</CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">* Stock carregado automaticamente da tabela de Stocks (Admin)</p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-7 gap-3 text-xs font-semibold mb-2">
                    <div>Valor</div>
                    <div>Stock</div>
                    <div>Receção</div>
                    <div>S.Inicial</div>
                    <div>S.Final</div>
                    <div>Vendidas</div>
                    <div>Total €</div>
                  </div>
                  <div className="space-y-3">
                    {[1, 2, 3, 5, 10, 15].map(valor => {
                      const stock = editData[`stock_${valor}`] || 0;
                      const recepcao = editData[`recepcao_${valor}`] || 0;
                      const sFinal = editData[`s_final_${valor}`] || 0;
                      const sInicial = stock + recepcao;
                      const vendidas = stock + recepcao - sFinal;
                      const total = vendidas * valor;
                      
                      return (
                        <div key={valor} className="grid grid-cols-7 gap-3 items-center border-b border-border pb-2">
                          <div className="font-semibold">{valor}€</div>
                          <Input
                            type="text"
                            value={stock}
                            onChange={(e) => updateEditDataNumeric(`stock_${valor}`, e.target.value)}
                            onKeyDown={handleNumericKeyDown}
                            inputMode="decimal"
                            className="text-center h-9"
                          />
                          <Input
                            type="text"
                            value={recepcao}
                            onChange={(e) => updateEditDataNumeric(`recepcao_${valor}`, e.target.value)}
                            onKeyDown={handleNumericKeyDown}
                            inputMode="decimal"
                            className="text-center h-9"
                          />
                          <div className="px-2 py-1 bg-muted rounded text-center">{sInicial}</div>
                          <Input
                            type="text"
                            value={sFinal}
                            onChange={(e) => updateEditDataNumeric(`s_final_${valor}`, e.target.value)}
                            onKeyDown={handleNumericKeyDown}
                            inputMode="decimal"
                            className="text-center h-9"
                          />
                          <div className="px-2 py-1 bg-success/10 text-success rounded text-center font-semibold">{vendidas}</div>
                          <div className="px-2 py-1 bg-success/10 text-success rounded text-center font-semibold">€{total.toFixed(2)}</div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Totais das Raspadinhas - IGUAL AO MODAL DE VER */}
                  <div className="mt-4 pt-4 border-t-2 border-border">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-success/5 rounded-lg border border-success/20">
                        <label className="block text-sm font-medium mb-2 text-success">Total Vendidas (valor €)</label>
                        <div className="text-3xl font-bold text-success">
                          €{[1, 2, 3, 5, 10, 15].reduce((total, valor) => {
                            const stock = editData[`stock_${valor}`] || 0;
                            const recepcao = editData[`recepcao_${valor}`] || 0;
                            const sFinal = editData[`s_final_${valor}`] || 0;
                            const vendidas = stock + recepcao - sFinal;
                            return total + (vendidas * valor);
                          }, 0).toFixed(2)}
                        </div>
                      </div>
                      
                      <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                        <label className="block text-sm font-medium mb-2 text-primary">Total Stock Final (valor €)</label>
                        <div className="text-3xl font-bold text-primary">
                          €{[1, 2, 3, 5, 10, 15].reduce((total, valor) => 
                            total + ((editData[`s_final_${valor}`] || 0) * valor), 0
                          ).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Prémios */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Prémios e Vendas</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm mb-2">Vendas</label>
                      <Input
                        type="text"
                        
                        value={editData.premios_master_1_15 || 0}
                        onChange={(e) => updateEditDataNumeric('premios_master_1_15', e.target.value)}
                        onKeyDown={handleNumericKeyDown}
                        inputMode="decimal"
                      />
                    </div>
                    <div>
                      <label className="block text-sm mb-2">Prémios</label>
                      <Input
                        type="text"
                        
                        value={editData.premios_total || 0}
                        onChange={(e) => updateEditDataNumeric('premios_total', e.target.value)}
                        onKeyDown={handleNumericKeyDown}
                        inputMode="decimal"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Jogos Santa Casa */}
              {jogosData && jogosData.length > 0 && (
                <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <span className="text-2xl">🎰</span>
                      Jogos Santa Casa
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Editar valores dos jogos para {new Date(editData.data + 'T00:00:00').toLocaleDateString('pt-PT')}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {jogosData.map((jogo) => {
                        // Calcular qual semana do mês é a data do fecho
                        const dataFecho = new Date(editData.data + 'T00:00:00');
                        const dia = dataFecho.getDate();
                        const semanaNum = Math.ceil(dia / 7);
                        const semanaKey = `semana_${semanaNum}`;
                        
                        // Obter nome do mês em português
                        const meses = ["janeiro", "fevereiro", "marco", "abril", "maio", "junho",
                                      "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
                        const mesNome = meses[dataFecho.getMonth()];
                        
                        const valorAtual = jogo.dados_semanais?.[mesNome]?.[semanaKey] || 0;
                        
                        return (
                          <div key={jogo.id} className="p-4 bg-white rounded-lg border border-blue-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="mb-3">
                              <label className="block text-sm font-semibold mb-1 text-gray-700">{jogo.jogo}</label>
                              <p className="text-xs text-blue-600 font-medium">
                                {mesNome.charAt(0).toUpperCase() + mesNome.slice(1)} - Semana {semanaNum}
                              </p>
                            </div>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">€</span>
                              <Input
                                type="text"
                                
                                value={valorAtual}
                                onChange={(e) => {
                                  // Atualizar valor localmente - processar para aceitar vírgulas
                                  const valorProcessado = processNumericInput(e.target.value);
                                  const novosJogos = jogosData.map(j => {
                                    if (j.id === jogo.id) {
                                      return {
                                        ...j,
                                        dados_semanais: {
                                          ...j.dados_semanais,
                                          [mesNome]: {
                                            ...j.dados_semanais?.[mesNome],
                                            [semanaKey]: valorProcessado
                                          }
                                        }
                                      };
                                    }
                                    return j;
                                  });
                                  setJogosData(novosJogos);
                                }}
                                onKeyDown={handleNumericKeyDown}
                                inputMode="decimal"
                                className="text-right font-bold text-lg pl-8 bg-blue-50 border-blue-300 focus:border-blue-500"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Totais e Diferenças - CALCULADOS AUTOMATICAMENTE */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Totais e Diferenças</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Valor venda de raspadinhas</label>
                      <div className="px-4 py-6 border-2 rounded-lg bg-yellow-50 border-yellow-200">
                        <div className="text-3xl font-bold text-yellow-600">
                          €{calcularFechoTotais(editData, editData._dadosManha || null).valorVendaRaspadinhas.toFixed(2)}
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold mb-2">Total expectável</label>
                      <div className="px-4 py-6 border-2 rounded-lg bg-yellow-50/50 border-yellow-100">
                        <div className="text-3xl font-bold text-yellow-500">
                          €{calcularFechoTotais(editData, editData._dadosManha || null).totalExpectavel.toFixed(2)}
                        </div>
                        {editData.comissao_revendas > 0 && (
                          <p className="text-xs text-yellow-600 mt-1">
                            (Inclui €{((parseFloat(editData.comissao_revendas) || 0) / 0.05 * 0.95).toFixed(2)} de revendas)
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold mb-2">Caixa Final</label>
                      <Input
                        type="text"
                        
                        value={editData.caixa_final || 0}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value) || 0;
                          // 🔧 iter301 — sync bidirecional: alterar Caixa Final também
                          // sincroniza Caixa Atual (mesmo valor semanticamente).
                          setEditData({...editData, caixa_final: val, caixa_inicial_2: val});
                        }}
                        className="text-3xl font-bold text-blue-600 text-center h-20 bg-blue-50 border-2 border-blue-200"
                      />
                    </div>
                  </div>
                  
                  {(() => {
                    const t = calcularFechoTotais(editData, editData._dadosManha || null);
                    const valorAjustado = Math.abs(t.diferenca) < 0.01 ? 0 : t.diferenca;
                    const bgClass = t.diferenca >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
                    const txtClass = t.diferenca >= 0 ? 'text-green-600' : 'text-red-600';
                    return (
                      <div>
                        <label className="block text-sm font-semibold mb-2">Diferença</label>
                        <div className={`px-4 py-6 border-2 rounded-lg ${bgClass}`}>
                          <div className={`text-3xl font-bold ${txtClass}`}>
                            €{valorAjustado.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Observações */}
              <div>
                <label className="block text-sm font-medium mb-2">Observações</label>
                <textarea
                  value={editData.observacoes || ''}
                  onChange={(e) => setEditData({...editData, observacoes: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                  Cancelar
                </Button>
                <Button 
                  type="button" 
                  onClick={handleSaveEdit}
                  className="bg-primary hover:bg-primary/90"
                >
                  Guardar
                </Button>
              </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FechosTurnosTab;
